import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Pill } from '~/components/ui/nucleus/Pill';

type Props = {
  name?: string;
  /** Current value - empty string means OFF, non-empty means ON */
  value: string;
  label: string;
  /** Value to set when toggled ON */
  toggle: string;
  // Using any to avoid FontAwesome version mismatch issues between packages
  /** Icon to show when toggle is OFF (regular/outline) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: any;
  /** Icon to show when toggle is ON (solid/filled) - falls back to icon if not provided */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  iconActive?: any;
  onChange: (value: string) => void;
  /** If true, the toggle logic is inverted: empty = showing (default ON), '0' = hidden */
  invertedLogic?: boolean;
};

/**
 * A filter toggle pill that displays an icon.
 * Used for activity type filters like Messages, Status, Notes.
 *
 * By default (invertedLogic=false): empty value = inactive, toggle value = active
 * With invertedLogic=true: empty value = active (showing), '0' = inactive (hidden)
 */
export const IconFilterToggle = ({
  value,
  label,
  toggle,
  icon,
  iconActive,
  onChange,
  invertedLogic = false,
}: Props) => {
  // With inverted logic: no value or '1' means ON (showing), '0' means OFF (hidden)
  // Without inverted logic: value !== '' means ON
  const isActive = invertedLogic ? value !== '0' : value !== '';

  // Use solid icon when active, regular icon when inactive
  // Falls back to icon prop if iconActive not provided
  const currentIcon = isActive ? iconActive ?? icon : icon;

  const handleChange = () => {
    if (invertedLogic) {
      // Toggle between '' (showing/default) and '0' (hidden)
      onChange(isActive ? '0' : '');
    } else {
      // Toggle between '' (inactive) and toggle value (active)
      onChange(value ? '' : toggle);
    }
  };

  return (
    <Pill
      as='label'
      type='filter'
      active={isActive}
      className='gap-1.5'
      tabIndex={0}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleChange();
        }
      }}
    >
      <input
        type='checkbox'
        className='hidden'
        checked={isActive}
        onChange={handleChange}
        aria-label={label}
      />
      {currentIcon && (
        <FontAwesomeIcon
          icon={currentIcon}
          className={classNames(
            'h-3.5 w-3.5 transition-colors',
            isActive
              ? 'text-white lg:text-secondary'
              : 'text-white opacity-70 lg:text-secondary lg:opacity-100'
          )}
        />
      )}
      <span className='truncate'>{label}</span>
    </Pill>
  );
};
